import { api, ApiError } from "./api.js";
import { toast } from "./utils.js";
import { getCurrentUser } from "./auth.js";

/**
 * The follow control, shared by the post card and the post detail header.
 *
 * Rendered only when the API told us the viewer's follow state. It omits
 * `is_following` for anonymous readers, and "we don't know" should render
 * no button rather than a wrong one. Also hidden on your own content,
 * since the API rejects following yourself with a 400.
 */
export function followButtonHtml(author, { extraClass = "ms-auto" } = {}) {
  const me = getCurrentUser();
  if (!author || !me || me.id === author.id) return "";
  if (typeof author.is_following !== "boolean") return "";

  const following = author.is_following;
  return `
    <button type="button"
            class="btn btn-sm ${following ? "btn-outline-secondary" : "btn-outline-primary"} ${extraClass} follow-btn"
            data-follow-user-id="${author.id}"
            data-following="${following}"
            aria-pressed="${following}">
      <i class="bi ${following ? "bi-person-check" : "bi-person-plus"} me-1"></i>${following ? "Following" : "Follow"}
    </button>`;
}

/**
 * Repaints every follow button for one author within `container`.
 *
 * A feed routinely shows several posts by the same person, and the detail
 * page shows the author in its header. Updating only the clicked button
 * would leave the others claiming the opposite, so all of them are found
 * by user id and rewritten together.
 */
export function paintFollowButtons(container, userId, following) {
  container
    .querySelectorAll(`.follow-btn[data-follow-user-id="${userId}"]`)
    .forEach((btn) => {
      btn.dataset.following = String(following);
      btn.setAttribute("aria-pressed", String(following));
      btn.disabled = false;
      const extra = btn.classList && btn.classList.contains("ms-auto") ? "ms-auto" : "";
      btn.className =
        `btn btn-sm ${following ? "btn-outline-secondary" : "btn-outline-primary"} ${extra} follow-btn`;
      btn.innerHTML =
        `<i class="bi ${following ? "bi-person-check" : "bi-person-plus"} me-1"></i>` +
        (following ? "Following" : "Follow");
    });
}

async function toggleFollow(container, btn) {
  const userId = btn.dataset.followUserId;
  const wasFollowing = btn.dataset.following === "true";

  // Optimistic: the round trip is short, but a button that does nothing
  // until the network answers reads as broken. Reverted below on failure.
  paintFollowButtons(container, userId, !wasFollowing);
  container
    .querySelectorAll(`.follow-btn[data-follow-user-id="${userId}"]`)
    .forEach((el) => (el.disabled = true));

  try {
    await api.post(`/users/${userId}/${wasFollowing ? "unfollow" : "follow"}`);
    paintFollowButtons(container, userId, !wasFollowing);
  } catch (err) {
    paintFollowButtons(container, userId, wasFollowing);
    toast(err instanceof ApiError ? err.message : "Could not update that follow.", "danger");
  }
}

/** Binds every follow button inside `container`. Safe to call after each
 * re-render; handlers attach to the current elements. */
export function attachFollowHandlers(container) {
  container.querySelectorAll(".follow-btn").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.stopPropagation(); // a post card is itself clickable
      toggleFollow(container, el);
    });
  });
}
